/**
 * Format a number as currency
 * @param {number|string} value - The value to format
 * @param {string} [currency='ILS'] - Currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'ILS') => {
  if (!value) return '';

  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Format a number as currency with no decimal places.
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "₪ 1,000")
 */
export const formatCurrencyShort = value => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format a currency delta with +/- sign prefix.
 * @param {number} value - The delta value
 * @returns {string} Formatted string (e.g., "+₪ 500", "-₪ 200")
 */
export const formatDelta = value => {
  const abs = Math.abs(value);
  const formatted = formatCurrencyShort(abs);
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatted}`;
};

/**
 * Parse a currency string back to a number
 * @param {string} value - Currency string to parse
 * @returns {number} Parsed number value
 */
export const parseCurrency = value => {
  if (!value) return '';
  return parseFloat(value.replace(/[^\d.-]/g, ''));
};

/**
 * Format a URL by ensuring it has https:// prefix
 * @param {string} value - The URL to format
 * @returns {string} Formatted URL with https:// prefix if needed
 */
export const formatUrl = value => {
  if (!value) return '';

  // Remove leading/trailing whitespace
  const trimmed = value.trim();

  // If it already has a protocol, return as is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Add https:// prefix
  return `https://${trimmed}`;
};

/**
 * Format a phone number for Israeli formats.
 * Mobile (05X prefix, 10 digits): 05X-XXX-XXXX
 * Landline (02/03/04/08/09 prefix, 9 digits): 0X-XXX-XXXX
 * @param {string} value - The phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = value => {
  if (!value) return '';

  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Mobile: starts with 05, 10 digits → 05X-XXX-XXXX
  if (digits.startsWith('05') && digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // Landline: starts with 02/03/04/08/09, 9 digits → 0X-XXX-XXXX
  if (/^0[23489]/.test(digits) && digits.length === 9) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5, 9)}`;
  }

  // Fallback: return digits grouped loosely (don't mangle unknown formats)
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

/**
 * Format a floor number as a Hebrew floor name
 * @param {number} floorNum - The floor number (0 = ground, negative = basement)
 * @returns {string} Hebrew floor name (e.g., "קומת קרקע", "קומה ראשונה")
 */
export const formatFloorName = floorNum => {
  if (floorNum === 0) return 'קומת קרקע';
  if (floorNum < 0) return 'קומת מרתף';

  const hebrewOrdinals = [
    'ראשונה',
    'שנייה',
    'שלישית',
    'רביעית',
    'חמישית',
    'שישית',
    'שביעית',
    'שמינית',
    'תשיעית',
    'עשירית',
  ];

  return floorNum <= 10 ? `קומה ${hebrewOrdinals[floorNum - 1]}` : `קומה ${floorNum}`;
};

/**
 * Validate an email address (basic format check)
 * @param {string} value - The email to validate
 * @returns {boolean} True if valid
 */
export const validateEmail = value => {
  if (!value) return true; // empty is OK (field is optional)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};
